import React from 'react';
import { Tool } from '@/app/types/cad';
import { Button, buttonVariants } from '@/app/components/ui/button';
import { Separator } from '@/app/components/ui/separator';
import { emitTutorialEvent } from '@/app/contexts/TutorialContext';
import {
  MousePointer2,
  Minus,
  Pentagon,
  Type,
  Hand,
  Undo2,
  Redo2,
  Grid3x3,
  Menu,
  Circle,
  Waves,
  CircleDot,
  Plug,
  ArrowLeftRight,
  Magnet,
  RedoDot, // Icon für Winkel-Tool
  Squircle, // Icon für Fillet-Tool (Abrunden)
  Spline, // Icon für Hut (Zentrierter Bogen)
  Music3, // Icon für Tangentialbogen
  Settings2, // Icon für Eigenschaften
  Axis3d, // Icon für Koordinatensystem
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/app/components/ui/tooltip';
import { useLanguage } from '@/app/contexts/LanguageContext';
import { cn } from '@/app/components/ui/utils';

interface ToolbarProps {
  currentTool: Tool;
  onToolChange: (tool: Tool) => void;
  onUndo: () => void;
  onRedo: () => void;
  onToggleDrawer: () => void;
  canUndo: boolean;
  canRedo: boolean;
  showGrid: boolean;
  onToggleGrid: () => void;
  snapEnabled: boolean;
  onToggleSnap: () => void;
  iconSize?: number; // Optional icon size in pixels (default: 20)
  trimLines?: boolean;
  onToggleTrimLines?: () => void;
  showProperties?: boolean;
  onToggleProperties?: () => void;
  showCoordinateSystem?: boolean;
  onToggleCoordinateSystem?: () => void;
}

export function Toolbar({
  currentTool,
  onToolChange,
  onUndo,
  onRedo,
  onToggleDrawer,
  canUndo,
  canRedo,
  showGrid,
  onToggleGrid,
  snapEnabled,
  onToggleSnap,
  iconSize = 20,
  trimLines = false,
  onToggleTrimLines,
  showProperties = true,
  onToggleProperties,
  showCoordinateSystem = false,
  onToggleCoordinateSystem,
}: ToolbarProps) {
  const { t } = useLanguage();

  const getButtonStyle = (variant: 'default' | 'ghost', size: 'sm') => {
    return cn(buttonVariants({ variant, size }));
  };

  // Wrapper für Tool-Wechsel mit Tutorial-Event
  const handleToolChange = (tool: Tool) => {
    onToolChange(tool);
    emitTutorialEvent({ type: 'tool-selected', tool });
  };

  return (
    <TooltipProvider>
      <div
        className="flex items-center gap-3 px-4 py-3 overflow-x-auto no-scrollbar"
        style={{
          backgroundColor: 'var(--md-surface-container)',
          boxShadow: 'var(--md-elevation-2)',
        }}
        data-tutorial="iso-toolbar"
      >
        {/* Hamburger Menu */}
        <Tooltip>
          <TooltipTrigger
            onClick={onToggleDrawer}
            className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), "rounded-full w-10 h-10 p-0")}
          >
            <Menu style={{ width: iconSize, height: iconSize }} />
          </TooltipTrigger>
          <TooltipContent>{t('editor.actions.openMenu')}</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="h-8" />

        {/* Undo/Redo */}
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger
              onClick={onUndo}
              disabled={!canUndo}
              className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), "rounded-full w-10 h-10 p-0")}
            >
              <Undo2 style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.actions.undo')} (Strg+Z)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={onRedo}
              disabled={!canRedo}
              className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), "rounded-full w-10 h-10 p-0")}
            >
              <Redo2 style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.actions.redo')} (Strg+Y)</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-8" />

        {/* Selection tools */}
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger
              onClick={() => handleToolChange('select')}
              className={cn(buttonVariants({ variant: currentTool === 'select' ? 'default' : 'ghost', size: 'sm' }))}
              data-tutorial="tool-select"
            >
              <MousePointer2 style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.select')} (V)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => handleToolChange('pan')}
              className={cn(buttonVariants({ variant: currentTool === 'pan' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Hand style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.pan')} (H)</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-8" />

        {/* Drawing tools */}
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger
              onClick={() => handleToolChange('line')}
              className={cn(buttonVariants({ variant: currentTool === 'line' ? 'default' : 'ghost', size: 'sm' }))}
              data-tutorial="tool-line"
            >
              <Minus style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.line')} (L)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => handleToolChange('shape')}
              className={cn(buttonVariants({ variant: currentTool === 'shape' ? 'default' : 'ghost', size: 'sm' }))}
              data-tutorial="tool-polygon"
            >
              <Pentagon style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.shape')} (P)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => handleToolChange('circle')}
              className={cn(buttonVariants({ variant: currentTool === 'circle' ? 'default' : 'ghost', size: 'sm' }))}
              data-tutorial="tool-circle"
            >
              <Circle style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.circle')} (C)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => handleToolChange('arc')}
              className={cn(buttonVariants({ variant: currentTool === 'arc' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <CircleDot style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.arc')} (A)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => handleToolChange('spline')}
              className={cn(buttonVariants({ variant: currentTool === 'spline' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Waves style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.spline')} (S)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => handleToolChange('fillet')}
              className={cn(buttonVariants({ variant: currentTool === 'fillet' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Squircle style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.fillet')} (F)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => handleToolChange('tangent-arc')}
              className={cn(buttonVariants({ variant: currentTool === 'tangent-arc' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Music3 style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.tangentArc')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => handleToolChange('centered-arc')}
              className={cn(buttonVariants({ variant: currentTool === 'centered-arc' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Spline style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.centeredArc')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => handleToolChange('text')}
              className={cn(buttonVariants({ variant: currentTool === 'text' ? 'default' : 'ghost', size: 'sm' }))}
              data-tutorial="tool-text"
            >
              <Type style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.text')} (T)</TooltipContent>
          </Tooltip>
        </div>

        {/* Tool Options */}
        {currentTool === 'fillet' && onToggleTrimLines && (
          <>
            <Separator orientation="vertical" className="h-8" />
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none font-medium px-2 py-1 rounded hover:bg-black/5" style={{ color: 'var(--md-on-surface)' }}>
                <input 
                  type="checkbox" 
                  checked={trimLines} 
                  onChange={onToggleTrimLines}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                {t('editor.actions.trim')}
              </label>
            </div>
          </>
        )}

        <Separator orientation="vertical" className="h-8" />

        {/* Connection tools */}
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger
              onClick={() => handleToolChange('connection')}
              className={cn(buttonVariants({ variant: currentTool === 'connection' ? 'default' : 'ghost', size: 'sm' }))}
              data-tutorial="tool-socket"
            >
              <Plug style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.connection')} (K)</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-8" />

        {/* Dimension tools */}
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger
              onClick={() => handleToolChange('dimension')}
              className={cn(buttonVariants({ variant: currentTool === 'dimension' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <ArrowLeftRight style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.dimension')} (D)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => handleToolChange('angle-dimension')}
              className={cn(buttonVariants({ variant: currentTool === 'angle-dimension' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <RedoDot style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.angle')} (W)</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-8" />

        {/* View controls */}
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger
              onClick={onToggleGrid}
              className={cn(buttonVariants({ variant: showGrid ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Grid3x3 style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.view.toggleGrid')} (G)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={onToggleSnap}
              className={cn(buttonVariants({ variant: snapEnabled ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Magnet style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.view.toggleSnap')}</TooltipContent>
          </Tooltip>
        </div>

        {onToggleProperties && (
          <>
            <Separator orientation="vertical" className="h-8" />
            <Tooltip>
              <TooltipTrigger
                onClick={onToggleProperties}
                className={cn(buttonVariants({ variant: showProperties ? 'default' : 'ghost', size: 'sm' }))}
              >
                <Settings2 style={{ width: iconSize, height: iconSize }} />
              </TooltipTrigger>
              <TooltipContent>{showProperties ? 'Eigenschaften ausblenden' : 'Eigenschaften anzeigen'}</TooltipContent>
            </Tooltip>
          </>
        )}

        {onToggleCoordinateSystem && (
          <>
            <Separator orientation="vertical" className="h-8" />
            <Tooltip>
              <TooltipTrigger
                onClick={onToggleCoordinateSystem}
                className={cn(buttonVariants({ variant: showCoordinateSystem ? 'default' : 'ghost', size: 'sm' }))}
              >
                <Axis3d style={{ width: iconSize, height: iconSize }} />
              </TooltipTrigger>
              <TooltipContent>{showCoordinateSystem ? 'Koordinatensystem ausblenden' : 'Koordinatensystem anzeigen'}</TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}